import {
  fetchVehicleTypesRequest,
  type VehicleTypesQuery,
} from '../../../graphql/vehicles/queries/fetchVehicleTypes.ts';
import { fetchVehicleTypeRequest } from '../../../graphql/vehicles/queries/fetchVehicleType.ts';
import type {
  VehicleTypeContext,
  VehicleType,
  PassengerCapacity,
  PropulsionType,
  FuelType,
  HybridCategory,
} from '../types/vehicleTypeTypes.ts';
import type { AccessToken } from '../../../auth';
import { FETCH_ALL_SIZE } from '../../../graphql/paginationTypes.ts';
import type { MultilingualString } from '../../netex/multilingualString.ts';
import { UNKNOWN_TRANSPORT_MODE } from '../../netex/transportMode.ts';

/** Drop server-side `null`s from a nullable list, coercing to `undefined` when empty. */
const compact = <T>(list?: (T | null)[] | null): T[] | undefined => {
  const out = list?.filter((x): x is T => x != null);
  return out && out.length ? out : undefined;
};

export const projectVehicleType = (
  vt: VehicleTypesQuery['vehicleTypes']['content'][number]
): VehicleType => ({
  id: vt.netexId,
  version: vt.version,
  name: vt.name ?? undefined,
  shortName: vt.shortName ?? undefined,
  description: vt.description ?? undefined,
  // Sobek's enum verbatim; null (Sobek does not default) → UNKNOWN.
  transportMode: vt.transportMode ?? UNKNOWN_TRANSPORT_MODE,
  length: vt.length ?? undefined,
  width: vt.width ?? undefined,
  height: vt.height ?? undefined,
  weight: vt.weight ?? undefined,
  lowFloor: vt.lowFloor ?? undefined,
  propulsionTypes: compact(vt.propulsionTypes),
  fuelTypes: compact(vt.fuelTypes),
  selfPropelled: vt.selfPropelled ?? undefined,
  euroClass: vt.euroClass ?? undefined,
  maximumVelocity: vt.maximumVelocity ?? undefined,
  maximumRange: vt.maximumRange ?? undefined,
  formDragCoefficient: vt.formDragCoefficient ?? undefined,
  rollResistanceCoefficient: vt.rollResistanceCoefficient ?? undefined,
  maximumEngineEffectKW: vt.maximumEngineEffectKW ?? undefined,
  hybridCategory: vt.hybridCategory ?? undefined,
  passengerCapacity: vt.passengerCapacity ?? undefined,
  created: vt.created ?? undefined,
  changed: vt.changed ?? undefined,
  changedBy: vt.changedBy ?? undefined,
  deckPlan: vt.deckPlan
    ? { id: vt.deckPlan.netexId, name: vt.deckPlan.name ?? undefined, version: vt.deckPlan.version }
    : undefined,
  vehicles: vt.vehicles
    ? vt.vehicles.map(v => ({
        id: v.netexId,
        registrationNumber: v.registrationNumber,
        operationalNumber: v.operationalNumber ?? undefined,
        version: v.version,
      }))
    : undefined,
});

/**
 * Sobek `VehicleTypeInput` — the mutation-accepted shape. Strict subset of the
 * fetched `VehicleTypesQuery` row: no `version`/`created`/`changed`/`changedBy`
 * /`vehicles` (all server-managed; Sobek resolves the live version by `netexId`)
 * and it adds `description`. Mirrors `input VehicleTypeInput` in the SDL
 * (`src/graphql/sobek.schema.graphqls`).
 */
export interface VehicleTypeInput {
  netexId?: string | null;
  /** Owning organisation ref (NeTEx codespace). Required by Sobek `VehicleTypeInput`. */
  dataOwnerRef: string;
  name?: MultilingualString | null;
  shortName?: MultilingualString | null;
  description?: MultilingualString | null;
  euroClass?: string | null;
  propulsionTypes?: (PropulsionType | null)[] | null;
  fuelTypes?: (FuelType | null)[] | null;
  transportMode?: string | null;
  passengerCapacity?: PassengerCapacity | null;
  deckPlan?: { netexId: string } | null;
  formDragCoefficient?: number | null;
  rollResistanceCoefficient?: number | null;
  maximumEngineEffectKW?: number | null;
  maximumVelocity?: number | null;
  maximumRange?: number | null;
  length?: number | null;
  height?: number | null;
  width?: number | null;
  weight?: number | null;
  lowFloor?: boolean | null;
  selfPropelled?: boolean | null;
  hybridCategory?: HybridCategory | null;
}

/**
 * Inverse of {@link projectVehicleType}: domain {@link VehicleType} →
 * {@link VehicleTypeInput} for `createOrUpdateVehicleType`. Sobek does a
 * **full-document replace**, so every input-accepted field is emitted —
 * `undefined` is coerced to explicit `null` so a blanked field is cleared
 * rather than silently retained (the opposite of `useVehiclePairSave`'s
 * omit-blank). `version`/`vehicles`/audit fields are intentionally dropped
 * (not part of the input contract). Environmental extras
 * (`formDragCoefficient`, `rollResistanceCoefficient`, `maximumEngineEffectKW`,
 * `hybridCategory`) are first-class on Sobek's schema — Sobek surface-lifts
 * them from the underlying `keyValues` bag — so they round-trip directly here.
 * `privateCode` and the rest of `keyValues` (notably the Autosys `imported-id`
 * provenance link) are not modelled or emitted: the input shape has no
 * `keyValues` field (sobek#149 — echoing one trips `INTERNAL_ERROR`), so any
 * unlifted keyValues entry is cleared by full-replace on save.
 *
 * `deckPlan` is emitted as a Sobek `DeckPlanReferenceInput` (`netexId` only —
 * `name` is not part of the reference input). `dataOwnerRef` is a required
 * field on the input and is threaded in by the caller (current organisation).
 *
 * @param vt Domain VehicleType (as held by the editor form).
 * @param dataOwnerRef Owning organisation ref (required by Sobek's input).
 * @returns The mutation input payload.
 */
export const serializeVehicleType = (vt: VehicleType, dataOwnerRef: string): VehicleTypeInput => ({
  netexId: vt.id === '' ? undefined : vt.id,
  dataOwnerRef,
  name: vt.name ?? null,
  shortName: vt.shortName ?? null,
  description: vt.description ?? null,
  euroClass: vt.euroClass ?? null,
  propulsionTypes: vt.propulsionTypes ?? null,
  fuelTypes: vt.fuelTypes ?? null,
  transportMode: vt.transportMode ?? null,
  passengerCapacity: vt.passengerCapacity ?? null,
  deckPlan: vt.deckPlan ? { netexId: vt.deckPlan.id } : null,
  formDragCoefficient: vt.formDragCoefficient ?? null,
  rollResistanceCoefficient: vt.rollResistanceCoefficient ?? null,
  maximumEngineEffectKW: vt.maximumEngineEffectKW ?? null,
  maximumVelocity: vt.maximumVelocity ?? null,
  maximumRange: vt.maximumRange ?? null,
  length: vt.length ?? null,
  height: vt.height ?? null,
  width: vt.width ?? null,
  weight: vt.weight ?? null,
  lowFloor: vt.lowFloor ?? null,
  selfPropelled: vt.selfPropelled ?? null,
  hybridCategory: vt.hybridCategory ?? null,
});

/**
 * Fetch the full VehicleType list (plus nested deckPlan + vehicles). Sobek's
 * post-#135 schema returns `netexId` in full NeTEx form at every level, so the
 * projection just renames `netexId` → `id` and coerces server-side `null` to
 * `undefined` to match the internal optional-shape contract.
 *
 * @param applicationBaseUrl Sobek base URL.
 * @param token OIDC access token (bearer).
 */
export const fetchVehicleTypes = async (
  applicationBaseUrl: string,
  dataOwnerRef: string,
  token: AccessToken
): Promise<VehicleTypeContext> => {
  const raw = await fetchVehicleTypesRequest(applicationBaseUrl, token, {
    size: FETCH_ALL_SIZE,
    filter: { dataOwnerRef },
  });
  return { vehicleTypes: raw.vehicleTypes.content.map(projectVehicleType) };
};

/**
 * Fetch one VehicleType by NeTEx id — the `vehicleTypes(...)` query filtered
 * to `netexIds: [id]`.
 *
 * @param netexId Full NeTEx id.
 * @param applicationBaseUrl Sobek base URL.
 * @param dataOwnerRef Current organisation ref.
 * @param token OIDC access token (bearer).
 * @returns The vehicle type, or `null` when Sobek returns none for the id.
 */
export const fetchVehicleType = async (
  netexId: string,
  applicationBaseUrl: string,
  dataOwnerRef: string,
  token: AccessToken
): Promise<VehicleType | null> => {
  const raw = await fetchVehicleTypeRequest(applicationBaseUrl, netexId, dataOwnerRef, token);
  return raw.vehicleTypes.content.map(projectVehicleType).find(vt => vt.id === netexId) ?? null;
};
